/* GW Quiz + Calculator + Qty + Add to cart + Mini-cart open
   - Quiz P1–P5 (como lo definimos)
   - #1 fijo, #2/#3 rotan (pool 3–6 máx por perfil), estable por sesión
   - Add to cart: refresca mini-cart (section_id=mini-cart) y abre drawer
*/
(() => {
  const root = document.getElementById('gw-quiz-root');
  if (!root) return;

  /* ===== Calculator helpers ===== */
  const GWQ_PANEL_SQFT_DEFAULT = 10.76; // premium/elite
  const GWQ_PANEL_SQFT_INDOOR  = 10.32; // indoorboxwood
  const GWQ_WASTAGE_MULT = 1.10;

  const GWQ_TIER_SCHEMES = {
    premium: [
      { min: 1,  max: 12,       discount: 0.00 },
      { min: 13, max: 24,       discount: 1.00 },
      { min: 25, max: 36,       discount: 2.00 },
      { min: 37, max: 48,       discount: 3.00 },
      { min: 49, max: Infinity, discount: 4.00 }
    ],
    elite: [
      { min: 1,  max: 12,       discount: 0.00 },
      { min: 13, max: 24,       discount: 1.50 },
      { min: 25, max: 36,       discount: 3.00 },
      { min: 37, max: 48,       discount: 4.50 },
      { min: 49, max: Infinity, discount: 6.00 }
    ],
    none: [{ min: 1, max: Infinity, discount: 0.00 }]
  };

  function gwqFindTier(q, tiers){
    return (tiers || []).find(t => q >= t.min && q <= t.max) || { discount: 0 };
  }

  function gwqDetectScheme(product){
    const tags = (product && product.tags) ? product.tags.map(t => String(t).toLowerCase()) : [];
    if (tags.includes('discount:premium')) return 'premium';
    if (tags.includes('discount:elite')) return 'elite';

    // fallback por precio (como tu snippet)
    const priceCents = product?.variants?.[0]?.price;
    const sale = typeof priceCents === 'number' ? (priceCents / 100) : 0;
    if (sale === 49.99) return 'premium';
    if (sale === 81.99) return 'elite';

    return 'none';
  }

  function gwqPanelSqftByHandle(handle){
    return (handle === 'indoorboxwood') ? GWQ_PANEL_SQFT_INDOOR : GWQ_PANEL_SQFT_DEFAULT;
  }

  function gwqUnitPriceForQty(product, qty){
    const scheme = gwqDetectScheme(product);
    const priceCents = product?.variants?.[0]?.price;
    const sale = typeof priceCents === 'number' ? (priceCents / 100) : 0;

    if (scheme === 'none') return Math.max(0.01, sale);

    const tiers = GWQ_TIER_SCHEMES[scheme] || GWQ_TIER_SCHEMES.none;
    const d = gwqFindTier(qty, tiers).discount || 0;
    return Math.max(0.01, sale - d);
  }

  function gwqMoney(v){
    const n = Math.round((v || 0) * 100) / 100;
    return `$${n.toFixed(2)} USD`;
  }

  /* ===== Settings (desde section settings) ===== */
  const SETTINGS = {
    heroImage: root.dataset.heroImage || '',
    heroTitle: root.dataset.heroTitle || 'Find Your Perfect Green Wall',
    heroSubtitle: root.dataset.heroSubtitle || '',
    startLabel: root.dataset.startLabel || 'Start Quiz',
    restartLabel: root.dataset.restartLabel || 'Restart',
    viewAllLabel: root.dataset.viewallLabel || 'View all panels',
    viewAllUrl: root.dataset.viewallUrl || '/collections/artificial-foliage-panels',
    resultsTitle: root.dataset.resultsTitle || 'Your best matches',
    resultsSubtitle: root.dataset.resultsSubtitle || ''
  };

  /* ===== Product handles ===== */
  const PRODUCT_HANDLES = [
    'indoorboxwood',
    'boxwood',
    'jasmine',
    'myrtle-leaf',
    'myrtle-flower',
    'gardenia-leaf',
    'gardenia-flower',
    'mixed-fern',
    'japanese-fern',
    'lavander-myrtle', // typo en URL real
    'red-myrtle',
    'tropical-mix',
    'black-forest'
  ];

  const WHY_COPY = {
    indoorboxwood: {
      why: 'Best value for indoor projects that need a clean green look.',
      bullets: ['Great for indoor use', 'Easy install', 'Simple, clean coverage']
    },
    boxwood: {
      why: 'A timeless, clean hedge look with strong coverage and easy pairing.',
      bullets: ['Clean & uniform', 'High coverage', 'Easy to cut and fit']
    },
    jasmine: {
      why: 'Adds softness and natural texture without looking too uniform.',
      bullets: ['Natural texture', 'Versatile look', 'Great everyday choice']
    },
    'myrtle-leaf': {
      why: 'Balanced greenery that feels natural and versatile in many spaces.',
      bullets: ['Natural texture', 'Great for most spaces', 'Easy to blend']
    },
    'myrtle-flower': {
      why: 'A subtle floral touch for a more decorative, refined finish.',
      bullets: ['Soft floral accents', 'Decor-forward', 'Great for feature areas']
    },
    'gardenia-leaf': {
      why: 'Clean, premium greenery with a polished, elegant look.',
      bullets: ['Premium finish', 'Elegant greenery', 'Easy to style']
    },
    'gardenia-flower': {
      why: 'White blooms add brightness and a standout decorative finish.',
      bullets: ['White bloom accents', 'High impact', 'Great for statement walls']
    },
    'mixed-fern': {
      why: 'Layered foliage that reads “lush” on camera and in real life.',
      bullets: ['Lush layered look', 'Photo-friendly', 'Great for feature walls']
    },
    'japanese-fern': {
      why: 'Fresh, airy texture that feels modern and organic.',
      bullets: ['Modern texture', 'Nice depth', 'Great outdoors']
    },
    'lavander-myrtle': {
      why: 'Color accents that add personality while keeping a green foundation.',
      bullets: ['Color accents', 'Decorative look', 'Still feels green']
    },
    'red-myrtle': {
      why: 'Bolder color accents designed for feature walls and brand moments.',
      bullets: ['High-impact accents', 'Statement-ready', 'Very photo-friendly']
    },
    'tropical-mix': {
      why: 'Elite-level “wow” factor with premium depth and rich composition.',
      bullets: ['Maximum impact', 'Rich texture', 'Perfect for events']
    },
    'black-forest': {
      why: 'Dense, dramatic look with premium depth and contrast.',
      bullets: ['High density', 'Strong depth', 'Statement wall']
    }
  };

  /* ===== New Questions (P1–P5) ===== */
  const QUESTIONS = [
    {
      id: 'project',
      title: 'P1 — Type of project',
      options: [
        { id: 'residential', label: 'Residential', emoji: '🏠' },
        { id: 'commercial', label: 'Commercial', emoji: '🏢' },
        { id: 'event', label: 'Event', emoji: '🎥' },
        { id: 'interior_decor', label: 'Interior decoration', emoji: '🛋️' },
        { id: 'exterior_privacy', label: 'Exterior privacy', emoji: '🔒' }
      ]
    },
    {
      id: 'location',
      title: 'P2 — Location',
      options: [
        { id: 'indoor', label: 'Indoor', emoji: '🏠' },
        { id: 'outdoor', label: 'Outdoor', emoji: '🌤️' },
        { id: 'both', label: 'Both', emoji: '↔️' }
      ]
    },
    {
      id: 'objective',
      title: 'P3 — Main objective',
      options: [
        { id: 'privacy', label: 'Privacy', emoji: '🔒' },
        { id: 'decor', label: 'Decoration', emoji: '✨' },
        { id: 'impact', label: 'Visual impact', emoji: '🔥' },
        { id: 'neutral', label: 'Neutral background', emoji: '🟩' }
      ]
    },
    {
      id: 'style',
      title: 'P4 — Style',
      options: [
        { id: 'natural_clean', label: 'Natural & clean', emoji: '🌿' },
        { id: 'tropical_lush', label: 'Tropical & lush', emoji: '🌴' },
        { id: 'with_flowers', label: 'With flowers', emoji: '🌸' },
        { id: 'with_color', label: 'With color', emoji: '🎨' }
      ]
    },
    {
      id: 'budget',
      title: 'P5 — Budget',
      options: [
        { id: 'basic', label: 'Basic', emoji: '💰' },
        { id: 'balanced', label: 'Balanced', emoji: '⚖️' },
        { id: 'premium', label: 'Premium', emoji: '👑' }
      ]
    }
  ];

  const state = {
    screen: 'hero', // hero | quiz | results
    idx: 0,
    answers: {
      project: null,
      location: null,
      objective: null,
      style: null,
      budget: null
    },
    products: new Map()
  };

  /* ===== Stable variation seed (session-based) ===== */
  const GWQ_SESSION_SEED = (() => {
    try {
      const k = 'gwq_seed_v2';
      const existing = sessionStorage.getItem(k);
      if (existing) return Number(existing) || 1;
      const v = Math.floor(Math.random() * 0x7fffffff) + 1;
      sessionStorage.setItem(k, String(v));
      return v;
    } catch (e) {
      return 1234567;
    }
  })();

  function gwqHashString(str){
    let h = 2166136261;
    for (let i = 0; i < str.length; i++){
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function gwqMulberry32(seed){
    let a = seed >>> 0;
    return function(){
      a |= 0;
      a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function gwqShuffleDeterministic(arr, seed){
    const rnd = gwqMulberry32(seed);
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--){
      const j = Math.floor(rnd() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  /* ===== Hard exclusions (mutually exclusive) ===== */
  const GWQ_EXCLUSION_GROUPS = [
    ['boxwood', 'jasmine'],
    ['myrtle-leaf', 'myrtle-flower'],
    ['gardenia-leaf', 'gardenia-flower']
  ];

  function gwqIsCompatible(currentHandles, candidate){
    for (const group of GWQ_EXCLUSION_GROUPS){
      if (group.includes(candidate)){
        for (const chosen of currentHandles){
          if (group.includes(chosen)) return false;
        }
      }
    }
    return true;
  }

  /* ===== Fetch products ===== */
  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed ${handle}`);
    return res.json();
  }

  async function loadProducts() {
    await Promise.all(PRODUCT_HANDLES.map(async (h) => {
      try {
        const p = await fetchProduct(h);
        state.products.set(h, p);
      } catch (e) {
        console.warn('GW Quiz product fetch failed:', h, e);
      }
    }));
  }

  function getFeaturedImage(p) {
    if (!p) return '';
    if (Array.isArray(p.images) && p.images.length) return p.images[0];
    if (p.featured_image) return p.featured_image;
    return '';
  }

  function getFirstAvailableVariantId(p) {
    if (!p || !Array.isArray(p.variants)) return null;
    const v = p.variants.find(x => x.available) || p.variants[0];
    return v ? v.id : null;
  }

  /* ===== Scoring logic (P1–P5) ===== */
  function scoreHandles(answers) {
    const score = {};
    PRODUCT_HANDLES.forEach(h => (score[h] = 0));

    const project   = answers.project;
    const location  = answers.location;
    const objective = answers.objective;
    const style     = answers.style;
    const budget    = answers.budget;

    // Safety: indoor-only product
    const forceNoIndoorBoxwood = (location === 'outdoor') || (project === 'exterior_privacy');
    if (forceNoIndoorBoxwood) score.indoorboxwood = -999;

    /* --- P1 Project type --- */
    if (project === 'residential') {
      score.boxwood += 16; score.jasmine += 16; score['myrtle-leaf'] += 14;
      score['gardenia-leaf'] += 10; score['mixed-fern'] += 10; score.indoorboxwood += 10;
      score['black-forest'] += 6; score['tropical-mix'] += 6;
    }
    if (project === 'commercial') {
      score['black-forest'] += 22; score['tropical-mix'] += 22; score['mixed-fern'] += 14;
      score.boxwood += 12; score['gardenia-flower'] += 10; score['red-myrtle'] += 10;
      score.indoorboxwood = -999;
    }
    if (project === 'event') {
      score['tropical-mix'] += 26; score['mixed-fern'] += 22; score['black-forest'] += 16;
      score['gardenia-flower'] += 14; score['red-myrtle'] += 12; score['japanese-fern'] += 10;
      score.indoorboxwood += 6;
    }
    if (project === 'interior_decor') {
      score.indoorboxwood += 24; score.boxwood += 18; score.jasmine += 16;
      score['myrtle-leaf'] += 14; score['gardenia-leaf'] += 14; score['myrtle-flower'] += 10;
      score['gardenia-flower'] += 10;
    }
    if (project === 'exterior_privacy') {
      score.boxwood += 26; score['black-forest'] += 22; score['tropical-mix'] += 18;
      score.jasmine += 10; score['mixed-fern'] += 10; score['japanese-fern'] += 10;
      score.indoorboxwood = -999;
    }

    /* --- P2 Location --- */
    if (location === 'indoor') {
      score.indoorboxwood += 18; score.boxwood += 14; score.jasmine += 14;
      score['myrtle-leaf'] += 12; score['gardenia-leaf'] += 12; score['gardenia-flower'] += 10;
      score['mixed-fern'] += 8;
    }
    if (location === 'outdoor') {
      score['japanese-fern'] += 18; score['mixed-fern'] += 16; score.boxwood += 14;
      score['tropical-mix'] += 16; score['black-forest'] += 14; score['red-myrtle'] += 10;
      score['lavander-myrtle'] += 8;
      score.indoorboxwood = -999;
    }
    if (location === 'both') {
      score.boxwood += 16; score.jasmine += 14; score['myrtle-leaf'] += 12;
      score['mixed-fern'] += 12; score['japanese-fern'] += 10; score['black-forest'] += 8;
    }

    /* --- P3 Objective --- */
    if (objective === 'privacy') {
      score.boxwood += 24; score['black-forest'] += 20; score['tropical-mix'] += 16;
      score.jasmine += 12; score['myrtle-leaf'] += 10; score['mixed-fern'] += 10;
    }
    if (objective === 'decor') {
      score.jasmine += 18; score['gardenia-leaf'] += 14; score['myrtle-flower'] += 14;
      score['gardenia-flower'] += 14; score['lavander-myrtle'] += 12; score['myrtle-leaf'] += 10;
    }
    if (objective === 'impact') {
      score['tropical-mix'] += 26; score['black-forest'] += 22; score['mixed-fern'] += 18;
      score['gardenia-flower'] += 16; score['red-myrtle'] += 14;
    }
    if (objective === 'neutral') {
      score.boxwood += 22; score.indoorboxwood += 20; score['gardenia-leaf'] += 14;
      score['myrtle-leaf'] += 12; score.jasmine += 10;
    }

    /* --- P4 Style --- */
    if (style === 'natural_clean') {
      score.boxwood += 24; score.indoorboxwood += 20; score['gardenia-leaf'] += 16;
      score['myrtle-leaf'] += 14; score.jasmine += 12;
    }
    if (style === 'tropical_lush') {
      score['tropical-mix'] += 24; score['mixed-fern'] += 22; score['japanese-fern'] += 18;
      score['black-forest'] += 16;
    }
    if (style === 'with_flowers') {
      score['gardenia-flower'] += 24; score['myrtle-flower'] += 20; score['black-forest'] += 14;
      score['tropical-mix'] += 10;
    }
    if (style === 'with_color') {
      score['red-myrtle'] += 24; score['lavander-myrtle'] += 20; score['tropical-mix'] += 14;
      score['black-forest'] += 10;
    }

    /* --- P5 Budget --- */
    if (budget === 'basic') {
      score.indoorboxwood += (location === 'indoor' ? 28 : -999);
      score.boxwood += 12; score.jasmine += 12; score['myrtle-leaf'] += 10;
      score['gardenia-leaf'] += 8;
      score['tropical-mix'] -= 10; score['black-forest'] -= 10;
    }
    if (budget === 'balanced') {
      score.jasmine += 18; score['myrtle-leaf'] += 16; score.boxwood += 14;
      score['mixed-fern'] += 12; score['japanese-fern'] += 12; score['lavander-myrtle'] += 10;
    }
    if (budget === 'premium') {
      score['black-forest'] += 26; score['tropical-mix'] += 24; score['mixed-fern'] += 14;
      score['gardenia-flower'] += 12; score['red-myrtle'] += 10;
      score.indoorboxwood -= 6;
    }

    // Final safety
    if (forceNoIndoorBoxwood) score.indoorboxwood = -999;

    const ranked = Object.keys(score)
      .filter(h => score[h] > -500)
      .sort((a, b) => score[b] - score[a]);

    return { score, ranked };
  }

  // Pool 3–6 max por perfil: primary + 2..5 alternatives cercanas
  function buildPool(ranked, score, primary){
    const base = score[primary] ?? 0;

    // 1) candidates within close range (tight)
    let candidates = ranked
      .filter(h => h !== primary)
      .filter(h => (score[h] ?? -999) >= base - 22);

    // 2) fallback relax if too few
    if (candidates.length < 2) {
      candidates = ranked
        .filter(h => h !== primary)
        .filter(h => (score[h] ?? -999) >= base - 36);
    }

    // Keep it compact (max 5 alternatives => total pool 6)
    candidates = candidates.slice(0, 5);

    return [primary, ...candidates];
  }

  // Picks 3: #1 fijo, #2/#3 rotan dentro del pool (session-stable) + exclusiones
  function pickRecommendations(answers){
    const { score, ranked } = scoreHandles(answers);
    if (!ranked.length) return [];

    const primary = ranked[0];
    const pool = buildPool(ranked, score, primary); // includes primary

    // seed depends on session + answers (stable per session)
    const key = `${answers.project}|${answers.location}|${answers.objective}|${answers.style}|${answers.budget}`;
    const seed = (GWQ_SESSION_SEED ^ gwqHashString(key)) >>> 0;

    const poolAlternatives = pool.filter(h => h !== primary);
    const shuffled = gwqShuffleDeterministic(poolAlternatives, seed);

    const chosen = [primary];

    for (const h of shuffled){
      if (chosen.length >= 3) break;
      if (!gwqIsCompatible(chosen, h)) continue;
      chosen.push(h);
    }

    // fallback from ranked if needed
    if (chosen.length < 3){
      for (const h of ranked){
        if (chosen.length >= 3) break;
        if (chosen.includes(h)) continue;
        if (!gwqIsCompatible(chosen, h)) continue;
        chosen.push(h);
      }
    }

    return chosen.slice(0, 3);
  }

  /* ===== Cart add ===== */
  async function addToCart(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  /* ===== Mini-cart refresh + open (mini-cart.liquid) ===== */
  async function gwqRefreshMiniCart() {
    // Tu section es mini-cart.liquid => section_id=mini-cart
    const res = await fetch(`/cart?section_id=mini-cart`, { credentials: 'same-origin' });
    if (!res.ok) return false;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Tu mini-cart: <form class="mini-cart ..." id="cart">
    const incoming = doc.querySelector('form.mini-cart#cart');
    const current  = document.querySelector('form.mini-cart#cart');

    if (incoming && current) {
      current.replaceWith(incoming);
      return true;
    }
    return false;
  }

  function gwqOpenMiniCartDrawer() {
    // 1) Custom element con open()
    const cartDrawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('[data-cart-drawer]') ||
      document.querySelector('[data-drawer="cart"]');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    // 2) Botones típicos de abrir drawer
    const opener =
      document.querySelector('[data-drawer-open="cart"]') ||
      document.querySelector('[data-cart-drawer-open]') ||
      document.querySelector('.header__icon--cart') ||
      document.querySelector('a.header__icon[href="/cart"]') ||
      document.querySelector('a[href="/cart"].header__icon') ||
      document.querySelector('a[href="/cart"]');

    if (opener) {
      opener.click();
      return true;
    }

    return false;
  }

  /* ===== Utils ===== */
  function htmlEscape(str) {
    return (str || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[m]));
  }

  function currentQuestion() {
    return QUESTIONS[state.idx];
  }

  // (Opcional UX) Si eligen “Exterior privacy”, fuerza location a outdoor.
  // Si eligen “Interior decoration”, fuerza location a indoor.
  function applyAutoConstraints(qid, aid){
    if (qid === 'project') {
      if (aid === 'exterior_privacy') state.answers.location = 'outdoor';
      if (aid === 'interior_decor') state.answers.location = 'indoor';
    }
  }

  /* ===== Calculator UI (per card) ===== */
  function renderCalcModal(handle){
    const safeHandle = htmlEscape(handle);
    return `
      <div class="gwq-calc-modal" id="gwqCalcModal-${safeHandle}" aria-hidden="true">
        <div class="gwq-calc-backdrop" data-action="calc-close" data-handle="${safeHandle}"></div>

        <div class="gwq-calc-dialog" role="dialog" aria-modal="true" aria-labelledby="gwqCalcTitle-${safeHandle}">
          <div class="gwq-calc-top">
            <h3 class="gwq-calc-h" id="gwqCalcTitle-${safeHandle}">How Many Pieces Do I Need?</h3>
            <button type="button" class="gwq-calc-close" aria-label="Close" data-action="calc-close" data-handle="${safeHandle}">×</button>
          </div>

          <div class="gwq-calc-body">
            <div class="gwq-field">
              <label class="gwq-label" for="gwqH-${safeHandle}">Wall Height (ft)</label>
              <input class="gwq-input" type="number" min="0" max="999" step="0.1" id="gwqH-${safeHandle}" placeholder="0">
            </div>

            <div class="gwq-field">
              <label class="gwq-label" for="gwqW-${safeHandle}">Wall Width (ft)</label>
              <input class="gwq-input" type="number" min="0" max="999" step="0.1" id="gwqW-${safeHandle}" placeholder="0">
            </div>

            <div class="gwq-overage">
              <label for="gwqOver-${safeHandle}">
                <input type="checkbox" id="gwqOver-${safeHandle}" checked>
                Include a 10% Overage
              </label>
            </div>

            <div class="gwq-note">
              <strong>Note:</strong> We recommend adding 10% extra material to account for cutting and installation waste.
            </div>

            <div class="gwq-result">
              <div class="gwq-pieces">Number of pieces: <span id="gwqPieces-${safeHandle}">0</span></div>
              <div class="gwq-small">Estimated total: <span id="gwqTotal-${safeHandle}">$0.00 USD</span></div>
            </div>

            <div class="gwq-err" id="gwqErr-${safeHandle}">Please enter valid height and width.</div>

            <button type="button"
                    class="gwq-apply"
                    data-action="calc-apply"
                    data-handle="${safeHandle}"
                    disabled>
              APPLY
            </button>
          </div>
        </div>
      </div>
    `;
  }

  function gwqOpenCalc(handle){
    const modal = document.getElementById(`gwqCalcModal-${handle}`);
    if (!modal) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => {
      const h = document.getElementById(`gwqH-${handle}`);
      h && h.focus({ preventScroll:true });
    }, 30);
    gwqRecalc(handle);
  }

  function gwqCloseCalc(handle){
    const modal = document.getElementById(`gwqCalcModal-${handle}`);
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function gwqRecalc(handle){
    const p = state.products.get(handle);
    const h = parseFloat(document.getElementById(`gwqH-${handle}`)?.value || '0');
    const w = parseFloat(document.getElementById(`gwqW-${handle}`)?.value || '0');
    const over = !!document.getElementById(`gwqOver-${handle}`)?.checked;

    const piecesEl = document.getElementById(`gwqPieces-${handle}`);
    const totalEl  = document.getElementById(`gwqTotal-${handle}`);
    const errEl    = document.getElementById(`gwqErr-${handle}`);
    const applyBtn = document.querySelector(`[data-action="calc-apply"][data-handle="${CSS.escape(handle)}"]`);

    if (!piecesEl || !totalEl || !applyBtn || !errEl) return;

    if (h <= 0 || w <= 0 || !p){
      piecesEl.textContent = '0';
      totalEl.textContent  = '$0.00 USD';
      applyBtn.disabled = true;
      errEl.classList.remove('is-on');
      return;
    }

    const sqftPerPanel = gwqPanelSqftByHandle(handle);
    const area = h * w;

    let rawPanels = area / sqftPerPanel;
    if (over) rawPanels *= GWQ_WASTAGE_MULT;
    const panels = Math.ceil(rawPanels);

    const unit = gwqUnitPriceForQty(p, panels);
    const total = unit * panels;

    piecesEl.textContent = String(panels);
    totalEl.textContent  = gwqMoney(total);
    applyBtn.disabled = false;
    errEl.classList.remove('is-on');
  }

  function gwqApplyCalcToQty(handle){
    const piecesEl = document.getElementById(`gwqPieces-${handle}`);
    const errEl    = document.getElementById(`gwqErr-${handle}`);
    const qtyInput = document.querySelector(`[data-qty-for="${CSS.escape(handle)}"]`);
    const panels = parseInt(piecesEl?.textContent || '0', 10);

    if (!qtyInput || !panels || panels < 1){
      errEl && errEl.classList.add('is-on');
      return;
    }

    qtyInput.value = String(panels);
    qtyInput.dispatchEvent(new Event('input',  { bubbles:true }));
    qtyInput.dispatchEvent(new Event('change', { bubbles:true }));

    gwqCloseCalc(handle);
  }

  /* ===== Render ===== */
  function render() {
    root.innerHTML = `
      <div class="gwq-wrap">
        ${renderHeroScreen()}
        ${renderQuizScreen()}
        ${renderResultsScreen()}
      </div>
    `;
    bindEvents();
  }

  function renderHeroScreen() {
    const withImage = !!SETTINGS.heroImage;
    const cls = withImage ? 'gwq-hero gwq-hero--with-image' : 'gwq-hero';

    return `
      <section class="gwq-screen ${state.screen === 'hero' ? 'is-active' : ''}" data-screen="hero">
        <div class="${cls}">
          ${withImage ? `
            <div class="gwq-hero__media">
              <img src="${htmlEscape(SETTINGS.heroImage)}" alt="${htmlEscape(SETTINGS.heroTitle)}" loading="lazy" decoding="async">
            </div>
          ` : ''}

          <div class="gwq-hero__content">
            <h2 class="gwq-title">${htmlEscape(SETTINGS.heroTitle)}</h2>
            ${SETTINGS.heroSubtitle ? `<p class="gwq-subtitle">${htmlEscape(SETTINGS.heroSubtitle)}</p>` : ''}
            <div class="gwq-center">
              <button type="button" class="button button--primary" data-action="start">${htmlEscape(SETTINGS.startLabel)}</button>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  function renderQuizScreen() {
    const q = currentQuestion();
    const total = QUESTIONS.length;
    const step = state.idx + 1;

    return `
      <section class="gwq-screen ${state.screen === 'quiz' ? 'is-active' : ''}" data-screen="quiz">
        <div class="gwq-quiz-wrap">
          <div class="gwq-panel">
            <div class="gwq-progress">
              <span>QUESTION ${step} OF ${total}</span>
              <span></span>
            </div>

            <h3 class="gwq-question">${htmlEscape(q?.title || '')}</h3>

            <div class="gwq-options">
              ${(q?.options || []).map(o => {
                const selected = state.answers[q.id] === o.id;
                return `
                  <button type="button"
                    class="gwq-option ${selected ? 'is-selected' : ''}"
                    data-q="${q.id}"
                    data-a="${o.id}"
                  >
                    <span class="gwq-emoji" aria-hidden="true">${o.emoji}</span>
                    <strong>${htmlEscape(o.label)}</strong>
                  </button>
                `;
              }).join('')}
            </div>

            <div class="gwq-center" style="margin-top:16px;">
              <button type="button" class="button button--secondary" data-action="back" ${state.idx === 0 ? 'disabled' : ''}>Back</button>
              <button type="button" class="button button--primary" data-action="next" ${state.answers[q?.id] ? '' : 'disabled'}>Next</button>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  function renderProductCard(handle, idx) {
    const p = state.products.get(handle);
    const img = getFeaturedImage(p);
    const url = p ? p.url : `/products/${handle}`;
    const title = p ? p.title : handle.replace(/-/g, ' ');
    const vId = getFirstAvailableVariantId(p);
    const copy = WHY_COPY[handle] || { why: 'A strong match for your choices.', bullets: [] };
    const pill = idx === 0 ? 'RECOMMENDED FOR YOU' : 'ALSO A GREAT OPTION';

    const safeHandle = htmlEscape(handle);

    return `
      <article class="gwq-product" data-handle="${safeHandle}">
        <div class="gwq-pill">${pill}</div>

        <div class="gwq-product__media">
          <a href="${htmlEscape(url)}" aria-label="${htmlEscape(title)}">
            <img class="gwq-product__img" src="${htmlEscape(img || '')}" alt="${htmlEscape(title)}" loading="lazy" decoding="async">
          </a>
        </div>

        <div class="gwq-product__body">
          <h4 class="gwq-product__name">${htmlEscape(title)}</h4>
          <p class="gwq-why">${htmlEscape(copy.why)}</p>

          ${copy.bullets && copy.bullets.length ? `
            <ul class="gwq-bullets">
              ${copy.bullets.slice(0, 3).map(b => `<li>${htmlEscape(b)}</li>`).join('')}
            </ul>
          ` : ''}

          <div class="gwq-actions">
            <button type="button"
              class="gwq-calc-open"
              data-action="calc-open"
              data-handle="${safeHandle}"
            >Calculate</button>

            <div class="gwq-qty">
              <button type="button" class="gwq-qty__btn" data-action="qty-minus" data-handle="${safeHandle}" aria-label="Decrease quantity">−</button>
              <input class="gwq-qty__input" type="number" min="1" value="1" inputmode="numeric" pattern="[0-9]*" data-qty-for="${safeHandle}">
              <button type="button" class="gwq-qty__btn" data-action="qty-plus" data-handle="${safeHandle}" aria-label="Increase quantity">+</button>
            </div>

            <button
              class="button button--primary gwq-add"
              type="button"
              data-action="add"
              data-handle="${safeHandle}"
              data-variant="${vId ? String(vId) : ''}"
              ${vId ? '' : 'disabled'}
            >Add to cart</button>
          </div>
        </div>

        ${renderCalcModal(handle)}
      </article>
    `;
  }

  function renderResultsScreen() {
    if (state.screen !== 'results') {
      return `<section class="gwq-screen" data-screen="results"></section>`;
    }

    const recommended = pickRecommendations(state.answers);
    const cards = recommended.map((h, i) => renderProductCard(h, i)).join('');

    return `
      <section class="gwq-screen is-active" data-screen="results">
        <div class="gwq-results-head">
          <div>
            <h3 class="gwq-results-title">${htmlEscape(SETTINGS.resultsTitle)}</h3>
            ${SETTINGS.resultsSubtitle ? `<p class="gwq-results-subtitle">${htmlEscape(SETTINGS.resultsSubtitle)}</p>` : ''}
          </div>

          <div class="gwq-center">
            <button type="button" class="button button--secondary" data-action="restart">${htmlEscape(SETTINGS.restartLabel)}</button>
            <a class="button button--tertiary" href="${htmlEscape(SETTINGS.viewAllUrl)}">${htmlEscape(SETTINGS.viewAllLabel)}</a>
          </div>
        </div>

        <div class="gwq-results-grid">
          ${cards}
        </div>
      </section>
    `;
  }

  /* ===== Events ===== */
  function bindEvents() {
    root.querySelectorAll('[data-action="start"]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.screen = 'quiz';
        state.idx = 0;
        render();
      });
    });

    root.querySelectorAll('[data-action="restart"]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.screen = 'hero';
        state.idx = 0;
        state.answers = { project:null, location:null, objective:null, style:null, budget:null };
        render();
      });
    });

    root.querySelectorAll('[data-action="back"]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (state.idx > 0) state.idx -= 1;
        render();
      });
    });

    root.querySelectorAll('[data-action="next"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const q = currentQuestion();
        if (!q || !state.answers[q.id]) return;

        if (state.idx < QUESTIONS.length - 1) {
          state.idx += 1;
          render();
          return;
        }

        state.screen = 'results';
        render();
      });
    });

    root.querySelectorAll('.gwq-option').forEach(btn => {
      btn.addEventListener('click', () => {
        const q = btn.getAttribute('data-q');
        const a = btn.getAttribute('data-a');
        if (!q || !a) return;

        state.answers[q] = a;
        applyAutoConstraints(q, a);

        render();
      });
    });

    // Qty +/-
    root.querySelectorAll('[data-action="qty-minus"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.getAttribute('data-handle');
        const input = root.querySelector(`[data-qty-for="${CSS.escape(handle)}"]`);
        if (!input) return;
        const v = Math.max(1, parseInt(input.value || '1', 10) - 1);
        input.value = String(v);
        input.dispatchEvent(new Event('change', { bubbles:true }));
      });
    });

    root.querySelectorAll('[data-action="qty-plus"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.getAttribute('data-handle');
        const input = root.querySelector(`[data-qty-for="${CSS.escape(handle)}"]`);
        if (!input) return;
        const v = Math.max(1, parseInt(input.value || '1', 10) + 1);
        input.value = String(v);
        input.dispatchEvent(new Event('change', { bubbles:true }));
      });
    });

    // Calculator open/close/apply + recalc events
    root.querySelectorAll('[data-action="calc-open"]').forEach(btn => {
      btn.addEventListener('click', () => gwqOpenCalc(btn.getAttribute('data-handle')));
    });

    root.querySelectorAll('[data-action="calc-close"]').forEach(btn => {
      btn.addEventListener('click', () => gwqCloseCalc(btn.getAttribute('data-handle')));
    });

    root.querySelectorAll('[data-action="calc-apply"]').forEach(btn => {
      btn.addEventListener('click', () => gwqApplyCalcToQty(btn.getAttribute('data-handle')));
    });

    root.querySelectorAll('input[id^="gwqH-"], input[id^="gwqW-"]').forEach(inp => {
      inp.addEventListener('input', () => {
        const id = inp.id || '';
        const handle = id.replace('gwqH-','').replace('gwqW-','');
        gwqRecalc(handle);
      });
    });

    root.querySelectorAll('input[id^="gwqOver-"]').forEach(inp => {
      inp.addEventListener('change', () => {
        const handle = (inp.id || '').replace('gwqOver-','');
        gwqRecalc(handle);
      });
    });

    // Escape closes calc
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = root.querySelector('.gwq-calc-modal.is-open');
      if (!open) return;
      const id = open.id || '';
      const handle = id.replace('gwqCalcModal-','');
      if (handle) gwqCloseCalc(handle);
    }, { once: true });

    // Add to cart (uses qty) + pulse + refresh mini-cart + open drawer
    root.querySelectorAll('[data-action="add"]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.getAttribute('data-variant');
        const handle = btn.getAttribute('data-handle');

        if (!variantId) {
          window.location.href = `/products/${handle}`;
          return;
        }

        const qtyInput = root.querySelector(`[data-qty-for="${CSS.escape(handle)}"]`);
        const qty = Math.max(1, parseInt(qtyInput?.value || '1', 10));

        const oldText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        try {
          await addToCart(Number(variantId), qty);

          // Latido 2 veces (via CSS keyframes)
          btn.classList.remove('is-pulse');
          void btn.offsetWidth; // reset anim
          btn.classList.add('is-pulse');
          setTimeout(() => btn.classList.remove('is-pulse'), 750);

          // Refresca mini-cart para que se vea inmediato
          await gwqRefreshMiniCart();

          // Abre el drawer
          setTimeout(() => gwqOpenMiniCartDrawer(), 30);

          btn.textContent = 'Added ✓';
          setTimeout(() => {
            btn.textContent = oldText;
            btn.disabled = false;
          }, 1200);
        } catch (e) {
          window.location.href = `/products/${handle}`;
        }
      });
    });
  }

  /* ===== Init ===== */
  (async function init() {
    await loadProducts();
    render();
  })();
})();
